import { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'ouaaa_notif_suggestion';
const FILTER_CHANGE_THRESHOLD = 5; // Show suggestion after 5 filter interactions
const DISMISS_COOLDOWN_DAYS = 7;

interface NotificationState {
  count: number;
  dismissed: string | null;
}

interface UseNotificationSuggestionReturn {
  showSuggestion: boolean;
  trackInteraction: () => void;
  dismiss: () => void;
  acknowledged: () => void;
}

function getState(): NotificationState | null {
  if (typeof window === 'undefined') return null;
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : { count: 0, dismissed: null };
  } catch {
    return { count: 0, dismissed: null };
  }
}

function saveState(state: NotificationState): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export default function useNotificationSuggestion(): UseNotificationSuggestionReturn {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const state = getState();
    if (!state) return;

    // Check if dismissed recently
    if (state.dismissed) {
      const dismissedDate = new Date(state.dismissed);
      const now = new Date();
      const daysSince = (now.getTime() - dismissedDate.getTime()) / (1000 * 60 * 60 * 24);
      if (daysSince < DISMISS_COOLDOWN_DAYS) return;
    }

    if (state.count >= FILTER_CHANGE_THRESHOLD) {
      setShow(true);
    }
  }, []);

  const trackInteraction = useCallback(() => {
    const state = getState();
    if (!state) return;
    state.count = (state.count || 0) + 1;
    saveState(state);

    if (state.count >= FILTER_CHANGE_THRESHOLD) {
      // Check cooldown
      if (state.dismissed) {
        const daysSince = (new Date().getTime() - new Date(state.dismissed).getTime()) / (1000 * 60 * 60 * 24);
        if (daysSince < DISMISS_COOLDOWN_DAYS) return;
      }
      setShow(true);
    }
  }, []);

  const dismiss = useCallback(() => {
    setShow(false);
    const state = getState();
    if (state) {
      state.dismissed = new Date().toISOString();
      state.count = 0;
      saveState(state);
    }
  }, []);

  const acknowledged = useCallback(() => {
    setShow(false);
    // Permanently hide by setting a far-future dismiss date
    const state = getState();
    if (state) {
      state.dismissed = new Date(2099, 0, 1).toISOString();
      state.count = 0;
      saveState(state);
    }
  }, []);

  return { showSuggestion: show, trackInteraction, dismiss, acknowledged };
}
